import React, { FC, useState } from 'react'
import { toast } from 'react-toastify'
import { deferPmsJob, deferPlanLineJob } from '../../core/pms/_requests'
import type { PmsJobRecord } from '../../core/pms/_models'

interface Props {
  visible: boolean
  job: PmsJobRecord | null
  onClose: () => void
  onSuccess: () => void
  locked?: boolean
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

export const DeferJobModal: FC<Props> = ({ visible, job, onClose, onSuccess, locked = false }) => {
  const isLocked = Boolean(locked)
  const [reason, setReason] = useState('')
  const [newDueDate, setNewDueDate] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setReason('')
    setNewDueDate('')
  }

  const handleSubmit = async () => {
    const planLineId = job?.planLineId || (job as any)?.__planLine?.id
    const isPeriodicityPseudoJob = !job?.id || job.id <= 0
    if (!job?.id && !planLineId) return
    if (isLocked) {
      toast.error('For this job to update, please contact your admin', { position: 'top-center' })
      return
    }
    if (!reason.trim()) {
      toast.error('Reason is required', { position: 'top-center' })
      return
    }
    if (!newDueDate.trim()) {
      toast.error('New Due Date is required', { position: 'top-center' })
      return
    }
    setSubmitting(true)
    try {
      if (isPeriodicityPseudoJob && planLineId) {
        await deferPlanLineJob(planLineId, reason.trim(), newDueDate)
      } else {
        await deferPmsJob(job!.id!, reason.trim(), newDueDate)
      }
      toast.success('Job deferred', { position: 'top-center' })
      onSuccess()
      reset()
    } catch (error: any) {
      toast.error('Failed to defer job', { position: 'top-center' })
    } finally {
      setSubmitting(false)
    }
  }

  if (!visible || !job) return null

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      role='dialog'
      aria-modal='true'
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ background: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-lg modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <div>
              <h5 className='modal-title text-dark'>Defer Job {job.jobCode || ''}</h5>
              {isLocked && (
                <div className='text-danger fs-7 mt-1'>
                  For this job to update, please contact your admin.
                </div>
              )}
            </div>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>
          <div className='modal-body'>
            {isLocked && (
              <div className='alert alert-warning py-2 px-3 mb-3'>
                For this job to update, please contact your admin.
              </div>
            )}
            <div className='mb-4'>
              <div className={`${LABEL} required`}>Reason</div>
              <textarea
                className='form-control'
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder='Enter reason for deferring this job'
                disabled={isLocked}
              />
            </div>
            <div className='row g-3'>
              <div className='col-md-6'>
                <div className={`${LABEL} required`}>New Due Date</div>
                <input
                  type='date'
                  className='form-control'
                  value={newDueDate}
                  onChange={(e) => setNewDueDate(e.target.value)}
                  required
                  disabled={isLocked}
                />
              </div>
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-warning'
              onClick={handleSubmit}
              disabled={submitting || isLocked || !reason.trim() || !newDueDate.trim()}
            >
              {submitting ? 'Deferring...' : 'Confirm Defer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
